/**
 * "Recently compared" for the Schema Compare setup dialog — a thin adapter
 * over the shared `RecentlyComparedPanel`, which owns all of the layout,
 * the "show N earlier" disclosure, the search box and the collapse-to-link
 * behaviour it shares with Data Compare (round-3 review R3-4/R3-10).
 *
 * This file's only job is Schema-Compare-specific: read the recents store,
 * describe each entry as a `RecentComparedItem` (endpoint detail = database
 * name, badge = whole-database vs single object), and fire this feature's
 * analytics events.
 */
import { useMemo } from 'react';
import { useConnectionStore } from '@/features/connections/store/connectionStore';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import {
  RecentlyComparedLinkButton,
  RecentlyComparedPanel,
  type RecentComparedItem,
  type RecentEntryPosition,
} from '@/features/workspace/common/components/RecentlyComparedPanel';
import {
  useRecentSchemaCompareStore,
  type RecentSchemaCompareEntry,
} from '../store/recentSchemaCompareStore';

const ID_PREFIX = 'recent-schema-compare';

type RecentlyComparedSectionProps = {
  onSelect: (entry: RecentSchemaCompareEntry) => void;
};

function toItem(entry: RecentSchemaCompareEntry, knownConnectionIds: Set<string>): RecentComparedItem {
  const sourceLabel = entry.sourceConnectionName || entry.sourceServerHost || 'Source';
  const targetLabel = entry.targetConnectionName || entry.targetServerHost || 'Target';
  const sourceDetail = entry.source.database ?? '';
  const targetDetail = entry.target.database ?? '';

  return {
    id: entry.id,
    sourceLabel,
    sourceDetail,
    targetLabel,
    targetDetail,
    badgeLabel: entry.compareWholeDatabase
      ? 'Whole DB'
      : entry.source.objectName
        ? `Object: ${entry.source.objectName}`
        : 'Object',
    comparedAt: entry.comparedAt,
    connectionExists:
      (entry.source.connectionId ? knownConnectionIds.has(entry.source.connectionId) : false) &&
      (entry.target.connectionId ? knownConnectionIds.has(entry.target.connectionId) : false),
    searchText: [
      entry.sourceConnectionName,
      entry.sourceServerHost,
      entry.source.database,
      entry.targetConnectionName,
      entry.targetServerHost,
      entry.target.database,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  };
}

/**
 * The collapsed affordance, rendered in the dialog header next to "Compare
 * whole database". Renders nothing when there is no history to offer.
 */
export function RecentlyComparedLink({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const hasEntries = useRecentSchemaCompareStore((s) => s.entries.length > 0);
  const { track } = useAnalytics();

  if (!hasEntries) return null;

  return (
    <RecentlyComparedLinkButton
      open={open}
      testId={`${ID_PREFIX}-link`}
      onToggle={() => {
        track('feature:schema_compare_recent_toggle', { enabled: !open });
        onToggle();
      }}
    />
  );
}

export function RecentlyComparedSection({ onSelect }: RecentlyComparedSectionProps) {
  const entries = useRecentSchemaCompareStore((s) => s.entries);
  const remove = useRecentSchemaCompareStore((s) => s.remove);
  const connections = useConnectionStore((s) => s.connections);
  const { track } = useAnalytics();

  const items = useMemo(() => {
    const knownConnectionIds = new Set(connections.map((c) => c.id));
    return entries.map((entry) => toItem(entry, knownConnectionIds));
  }, [entries, connections]);

  const handleSelect = (id: string, position: RecentEntryPosition) => {
    const entry = entries.find((e) => e.id === id);
    if (!entry) return;
    track('feature:schema_compare_recent_select', { position });
    onSelect(entry);
  };

  const handleRemove = (id: string) => {
    track('feature:schema_compare_recent_delete');
    remove(id);
  };

  return (
    <RecentlyComparedPanel
      items={items}
      sectionTestId="recently-compared-section"
      idPrefix={ID_PREFIX}
      searchPlaceholder="Search by connection or database…"
      onSelect={handleSelect}
      onRemove={handleRemove}
    />
  );
}
